import type { Directive } from "vue";
import parsePhoneNumber from "libphonenumber-js";

export type CellDataType =
  | "currency"
  | "text"
  | "percent"
  | "number"
  | "boolean"
  | "link"
  | "date"
  | "range"
  | "phone"
  | "email"
  | "url";
import moment from "moment-timezone";

export interface BaseCellDataTypeOptions {
  raw: string | number | boolean | Date | null | undefined;
  /**  Value to use if value is null or undefined */
  placeholder?: string;
}

// CURRENCY
export interface CurrencyCellDataTypeOptionsConfig {
  currency?: "USD";
  digits?: number;
}
export interface CurrencyCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    CurrencyCellDataTypeOptionsConfig {
  raw: number;
}

// NUMBER
export interface NumberCellDataTypeOptionsConfig {
  digits?: number;
}
export interface NumberCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    NumberCellDataTypeOptionsConfig {
  raw: number;
}

// PERCENT
export interface PercentCellDataTypeOptionsConfig {
  digits?: number;
}
export interface PercentCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    PercentCellDataTypeOptionsConfig {
  raw: number;
}

// BOOLEAN
export interface BooleanCellDataTypeOptionsConfig {
  trueLabel?: string;
  falseLabel?: string;
}
export interface BooleanCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    BooleanCellDataTypeOptionsConfig {
  raw: boolean;
}

// LINK
export interface LinkCellDataTypeOptionsConfig {
  textContent: string;
  target?: "_blank" | "_self";
  rel?: "noopener" | "noreferrer";
}
export interface LinkCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    LinkCellDataTypeOptionsConfig {
  raw: string;
}

// TEXT
export interface TextCellDataTypeOptionsConfig {}
export interface TextCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    TextCellDataTypeOptionsConfig {
  raw: string;
}

// DATE
export interface DateCellDataTypeOptionsConfig {
  mask: string;
}
export interface DateCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    DateCellDataTypeOptionsConfig {
  raw: string | Date | number;
}

// RANGE
export interface RangeCellDataTypeOptionsConfig {}
export interface RangeCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    RangeCellDataTypeOptionsConfig {
  raw: string;
  numberConfig?:
    | NumberCellDataTypeOptionsConfig
    | CurrencyCellDataTypeOptionsConfig;
}

// PHONE
export interface PhoneCellDataTypeOptionsConfig {}
export interface PhoneCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    PhoneCellDataTypeOptionsConfig {
  raw: string;
}

// EMAIL
export interface EmailCellDataTypeOptionsConfig {}
export interface EmailCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    EmailCellDataTypeOptionsConfig {
  raw: string;
}

// URL
export interface UrlCellDataTypeOptionsConfig {
  textContent?: string;
  target?: "_blank" | "_self";
  rel?: string;
}
export interface UrlCellDataTypeOptions
  extends BaseCellDataTypeOptions,
    UrlCellDataTypeOptionsConfig {
  raw: string;
}

export type CellDataTypeOptions =
  | CurrencyCellDataTypeOptions
  | NumberCellDataTypeOptions
  | PercentCellDataTypeOptions
  | BooleanCellDataTypeOptions
  | LinkCellDataTypeOptions
  | TextCellDataTypeOptions
  | DateCellDataTypeOptions
  | RangeCellDataTypeOptions;

export type CellDataTypeOptionConfigs =
  | CurrencyCellDataTypeOptionsConfig
  | NumberCellDataTypeOptionsConfig
  | PercentCellDataTypeOptionsConfig
  | BooleanCellDataTypeOptionsConfig
  | LinkCellDataTypeOptionsConfig
  | TextCellDataTypeOptionsConfig
  | DateCellDataTypeOptionsConfig
  | RangeCellDataTypeOptionsConfig
  | PhoneCellDataTypeOptionsConfig;

export const isValidNumber = (
  value: string | number | null | undefined
): boolean => {
  if (value === null || value === undefined) return false;

  // Parse string, currency, or number input
  const number =
    typeof value === "string"
      ? parseFloat(value.trim().replace(/[$,\s]/g, ""))
      : Number(value);

  return !Number.isNaN(number) && Number.isFinite(number);
};

export const formatCurrency = ({
  currency = "USD",
  digits = 2,
  raw,
  placeholder = "-"
}: CurrencyCellDataTypeOptions) => {
  if (!isValidNumber(raw)) return placeholder;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    maximumFractionDigits: digits
  }).format(+raw);
};

export const formatPercent = ({
  digits = 0,
  raw,
  placeholder = "-"
}: PercentCellDataTypeOptions) => {
  if (!isValidNumber(raw)) return placeholder;
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: digits
  }).format(+raw);
};

export const formatNumber = ({
  digits = 0,
  raw,
  placeholder = "-"
}: NumberCellDataTypeOptions) => {
  if (!isValidNumber(raw)) return placeholder;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits
  }).format(+raw);
};

export const formatBoolean = ({
  trueLabel = "Yes",
  falseLabel = "No",
  raw,
  placeholder = ""
}: BooleanCellDataTypeOptions) => {
  if (raw === null || raw === undefined) return placeholder;
  return raw ? trueLabel : falseLabel;
};

export const formatLink = (
  anchor: HTMLAnchorElement,
  options: LinkCellDataTypeOptions
) => {
  if (!(anchor instanceof HTMLAnchorElement))
    throw "[cell-formatter:link] requires an <a> tag";
  const a: HTMLAnchorElement = anchor as HTMLAnchorElement;
  a.href = options.raw;
  a.textContent = options.textContent;
  a.target = options.target || "_self";
  a.rel = options.rel || "noopener noreferrer";
};

export const formatText = ({
  raw,
  placeholder = ""
}: TextCellDataTypeOptions) => {
  if (raw === null || raw === undefined) return placeholder;
  return raw.toString();
};

export const formatDate = ({
  raw,
  mask,
  placeholder = ""
}: DateCellDataTypeOptions) => {
  if (raw === null || raw === undefined) return placeholder;
  const date = new Date(raw);
  return moment(date).format(mask);
};

export const formatRange = ({
  raw,
  placeholder = "",
  numberConfig
}: RangeCellDataTypeOptions) => {
  if (raw === null || raw === undefined) return placeholder;
  let formattedValue = raw.replace(/\s+/g, ""); // Remove spaces
  formattedValue = formattedValue.replace(/^(>=|<=|>|<|!=)(\d+)/, "$1 $2"); // Handle operators ("<=300" to "<= 300")
  formattedValue = formattedValue.replace(/^(\d+)-(\d+)$/, "$1 - $2"); // Handle numeric ranges ("250-300" to "250 - 300")
  if (!numberConfig) return formattedValue; // If no numberConfig, return pure range string digits
  // Format numbers within the range
  formattedValue = formattedValue.replace(/(\d+)/g, (s) =>
    "currency" in numberConfig
      ? formatCurrency({ raw: +s, ...numberConfig })
      : formatNumber({ raw: +s, ...numberConfig })
  );
  return formattedValue;
};

export const formatPhone = ({
  raw,
  placeholder = "-"
}: PhoneCellDataTypeOptions): string => {
  if (!raw) return placeholder;

  try {
    const phoneNumber = parsePhoneNumber(raw);
    if (!phoneNumber) return placeholder;
    return phoneNumber.formatNational();
  } catch {
    return placeholder;
  }
};

// Add new formatter functions
export const formatEmail = ({
  raw,
  placeholder = "-"
}: EmailCellDataTypeOptions): string => {
  if (!raw) return placeholder;

  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(raw)) return placeholder;

  return raw;
};

export const formatUrl = ({
  raw,
  placeholder = "-"
}: UrlCellDataTypeOptions): string => {
  if (!raw) return placeholder;

  try {
    new URL(raw);
    return raw;
  } catch {
    return placeholder;
  }
};

export const cellFormatterDirective: Directive<
  HTMLSpanElement | HTMLAnchorElement,
  CellDataTypeOptions,
  string,
  CellDataType
> = (el, binding) => {
  switch (binding.arg) {
    case "currency":
      el.textContent = formatCurrency(
        binding.value as CurrencyCellDataTypeOptions
      );
      break;
    case "percent":
      el.textContent = formatPercent(
        binding.value as PercentCellDataTypeOptions
      );
      break;
    case "number":
      el.textContent = formatNumber(binding.value as NumberCellDataTypeOptions);
      break;
    case "boolean":
      el.textContent = formatBoolean(
        binding.value as BooleanCellDataTypeOptions
      );
      break;
    case "link":
      formatLink(
        el as HTMLAnchorElement,
        binding.value as LinkCellDataTypeOptions
      );
      break;
    case "date":
      el.textContent = formatDate(binding.value as DateCellDataTypeOptions);
      break;
    case "range":
      el.textContent = formatRange(binding.value as RangeCellDataTypeOptions);
      break;
    case "phone":
      el.textContent = formatPhone(binding.value as PhoneCellDataTypeOptions);
      break;
    case "email":
      el.textContent = formatEmail(binding.value as EmailCellDataTypeOptions);
      break;
    case "url":
      if (el instanceof HTMLAnchorElement) {
        const options = binding.value as UrlCellDataTypeOptions;
        el.href = options.raw;
        el.textContent = options.textContent || options.raw;
        el.target = options.target || "_blank";
        el.rel = options.rel || "noopener noreferrer";
      } else {
        el.textContent = formatUrl(binding.value as UrlCellDataTypeOptions);
      }
      break;
    case "text":
    default:
      el.textContent = formatText(binding.value as TextCellDataTypeOptions);
  }
};
